/**
 * @fileoverview Aviso em tempo real para o dono da conversa quando chega uma OS
 * de um cliente que ele está atendendo: a triagem por IA não toma a conversa,
 * fica esperando, e a pessoa decide quando entregar à IA (decisão do dono,
 * 25/09/2026). O backend emite na sala pessoal `user-<id>`.
 */

import React, { useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ClipboardCheck } from "lucide-react";

import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const AUTO_CLOSE_MS = 20000;

const useStyles = makeStyles(() => ({
  container: { display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 2px" },
  icon: { color: "#fff", flexShrink: 0, marginTop: 2 },
  message: { fontSize: 13.5, lineHeight: 1.5, color: "#fff", marginBottom: 10 },
  actionBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12.5,
    borderRadius: 8,
    padding: "3px 14px",
    color: "#fff",
    border: "1.5px solid #fff",
    "&:hover": { border: "1.5px solid #fff", background: "rgba(255, 255, 255, 0.12)" },
  },
  "@global": {
    ".triage-notice-toast": {
      background: "#111318 !important",
      color: "#fff !important",
      borderRadius: "10px !important",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35) !important",
      border: "1px solid rgba(255, 255, 255, 0.14)",
    },
    ".triage-notice-toast .Toastify__close-button": { color: "#fff", opacity: 0.85, alignSelf: "flex-start" },
    ".triage-notice-toast .Toastify__progress-bar": { background: "rgba(255, 255, 255, 0.5)" },
  },
}));

// Texto vindo do backend (número da OS, nome da triagem) entra no fim, sem
// passar pela interpolação do i18next (que leria "{{…}}" dentro dele).
const MARCA_OS = "\u0001";
const MARCA_TRIAGEM = "\u0002";
const mensagem = (numero, triagem) =>
  i18n
    .t("triageNotice.message", { number: MARCA_OS, name: MARCA_TRIAGEM })
    .split(MARCA_OS)
    .join(String(numero || ""))
    .split(MARCA_TRIAGEM)
    .join(String(triagem || ""));

// A prop não pode se chamar "data": o react-toastify passa a dele por cima.
const Conteudo = ({ notice, onOpen }) => {
  const classes = useStyles();
  return (
    <div className={classes.container}>
      <ClipboardCheck size={26} className={classes.icon} aria-hidden="true" />
      <div>
        <div className={classes.message}>{mensagem(notice.workOrderNumber, notice.definitionName)}</div>
        <Button size="small" variant="outlined" className={classes.actionBtn} onClick={onOpen}>
          {i18n.t("triageNotice.open")}
        </Button>
      </div>
    </div>
  );
};

const TriageNoticeToast = () => {
  useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const history = useHistory();
  const companyId = user?.companyId;
  const userId = user?.id;
  const isInternalOnly = user?.accessScope === "internal";

  useEffect(() => {
    if (!companyId || !userId || isInternalOnly) return undefined;
    const socket = socketManager.getSocket(companyId);
    const onNotice = (data) => {
      if (data?.action !== "work_order_waiting" || !data.ticketUuid) return;
      const toastId = `triage-notice-${data.ticketId}`;
      const abrir = () => {
        toast.dismiss(toastId);
        history.push(`/tickets/${data.ticketUuid}`);
      };
      toast(<Conteudo notice={data} onOpen={abrir} />, {
        toastId,
        autoClose: AUTO_CLOSE_MS,
        className: "triage-notice-toast",
        closeOnClick: false,
      });
    };
    socket.on(`user-${userId}-triageNotice`, onNotice);
    return () => {
      // disconnect() do socket gerenciado só solta os ouvintes deste uso.
      socket.disconnect();
    };
  }, [companyId, userId, isInternalOnly, socketManager, history]);

  return null;
};

export default TriageNoticeToast;
